import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useApp } from '@/contexts/AppContext';
import type { UserRole } from '@/mocks/data';
import type { User } from '@/mocks/data';

const ROLE_BADGE_COLORS: Record<string, string> = {
  admin: 'bg-red-100 text-red-700 border-red-200',
  manager: 'bg-amber-100 text-amber-700 border-amber-200',
  receptionist: 'bg-sky-100 text-sky-700 border-sky-200',
  housekeeping: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  accountant: 'bg-violet-100 text-violet-700 border-violet-200',
};

function getRoleBadgeColor(role: string): string {
  return ROLE_BADGE_COLORS[role] || 'bg-teal-100 text-teal-700 border-teal-200';
}

export default function UserManagementPage() {
  const { allUsers, createUser, updateUser, deleteUser, getRoleLabel, permissions, allRoles } = useAuth();
  const { addToast } = useApp();

  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<User | null>(null);

  // Form state
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formFullName, setFormFullName] = useState('');
  const [formRole, setFormRole] = useState<UserRole>('receptionist');
  const [formPhone, setFormPhone] = useState('');

  const canManage = permissions?.manageUsers ?? false;

  if (!canManage) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-shield-user-line text-red-500 text-3xl" />
          </div>
          <h2 className="text-xl font-semibold text-navy-800 mb-2">Access Denied</h2>
          <p className="text-navy-500 text-sm">Only administrators can manage users.</p>
        </div>
      </div>
    );
  }

  const resetForm = () => {
    setFormEmail('');
    setFormPassword('');
    setFormFullName('');
    setFormRole('receptionist');
    setFormPhone('');
    setEditUser(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (user: User) => {
    setEditUser(user);
    setFormEmail(user.email);
    setFormPassword('');
    setFormFullName(user.full_name);
    setFormRole(user.role);
    setFormPhone(user.phone);
    setShowModal(true);
  };

  const handleSubmit = () => {
    if (!formEmail || !formFullName || !formPhone) {
      addToast('error', 'Please fill all required fields');
      return;
    }

    if (!editUser && !formPassword) {
      addToast('error', 'Password is required for new users');
      return;
    }

    if (editUser) {
      const updates: Partial<Omit<User, 'id' | 'created_at'>> = {
        email: formEmail,
        full_name: formFullName,
        role: formRole,
        phone: formPhone,
      };
      if (formPassword) {
        (updates as Record<string, string>).password = formPassword;
      }
      updateUser(editUser.id, updates);
      addToast('success', `User "${formFullName}" updated successfully`);
    } else {
      createUser({
        email: formEmail,
        password: formPassword,
        full_name: formFullName,
        role: formRole,
        phone: formPhone,
      });
      addToast('success', `User "${formFullName}" created successfully`);
    }

    setShowModal(false);
    resetForm();
  };

  const handleDelete = () => {
    if (!deleteConfirm) return;
    deleteUser(deleteConfirm.id);
    addToast('success', `User "${deleteConfirm.full_name}" deleted`);
    setDeleteConfirm(null);
  };

  return (
    <div className="min-h-screen bg-navy-50/30">
      {/* Page Header */}
      <div className="bg-white border-b border-navy-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl font-semibold text-navy-800">User Management</h1>
              <p className="text-xs text-navy-500 mt-0.5">Create, edit, and manage staff accounts</p>
            </div>
            <button
              onClick={openAddModal}
              className="flex items-center gap-2 px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              Add User
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-6">
        <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-navy-100 bg-navy-50/50">
                  <th className="text-left px-4 md:px-6 py-3 text-xs font-semibold text-navy-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="text-left px-4 md:px-6 py-3 text-xs font-semibold text-navy-500 uppercase tracking-wider hidden sm:table-cell">
                    Email
                  </th>
                  <th className="text-left px-4 md:px-6 py-3 text-xs font-semibold text-navy-500 uppercase tracking-wider hidden md:table-cell">
                    Phone
                  </th>
                  <th className="text-left px-4 md:px-6 py-3 text-xs font-semibold text-navy-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="text-left px-4 md:px-6 py-3 text-xs font-semibold text-navy-500 uppercase tracking-wider hidden lg:table-cell">
                    Created
                  </th>
                  <th className="text-right px-4 md:px-6 py-3 text-xs font-semibold text-navy-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-50">
                {allUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-navy-50/50 transition-colors">
                    <td className="px-4 md:px-6 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-navy-100 text-navy-700 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                          {u.full_name
                            .split(' ')
                            .map((n) => n[0])
                            .join('')}
                        </div>
                        <span className="font-medium text-navy-800">{u.full_name}</span>
                      </div>
                    </td>
                    <td className="px-4 md:px-6 py-3.5 text-navy-600 hidden sm:table-cell">
                      {u.email}
                    </td>
                    <td className="px-4 md:px-6 py-3.5 text-navy-600 hidden md:table-cell">
                      {u.phone}
                    </td>
                    <td className="px-4 md:px-6 py-3.5">
                      <span
                        className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(u.role)}`}
                      >
                        {getRoleLabel(u.role)}
                      </span>
                    </td>
                    <td className="px-4 md:px-6 py-3.5 text-navy-500 hidden lg:table-cell">
                      {new Date(u.created_at).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="px-4 md:px-6 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditModal(u)}
                          className="w-8 h-8 rounded-lg hover:bg-navy-50 flex items-center justify-center text-navy-500 hover:text-navy-700 transition-colors cursor-pointer"
                          title="Edit"
                        >
                          <i className="ri-edit-line" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(u)}
                          className="w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center text-navy-500 hover:text-red-600 transition-colors cursor-pointer"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary */}
        <div className="mt-4 text-xs text-navy-400">
          {allUsers.length} total users across {allRoles.length} roles
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg shadow-xl">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-navy-800">
                {editUser ? 'Edit User' : 'Add New User'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="w-8 h-8 rounded-lg hover:bg-navy-50 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-navy-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formFullName}
                  onChange={(e) => setFormFullName(e.target.value)}
                  className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                  placeholder="e.g. Rahul Sharma"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                  placeholder="e.g. rahul@hotelpro.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">
                  Password {!editUser && <span className="text-red-500">*</span>}
                  {editUser && <span className="text-navy-400 text-xs">(leave blank to keep current)</span>}
                </label>
                <input
                  type="text"
                  value={formPassword}
                  onChange={(e) => setFormPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                  placeholder={editUser ? 'Leave blank to keep current' : 'Enter password'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formPhone}
                  onChange={(e) => setFormPhone(e.target.value)}
                  className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                  placeholder="e.g. +91 98765 43210"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Role</label>
                <select
                  value={formRole}
                  onChange={(e) => setFormRole(e.target.value as UserRole)}
                  className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 bg-white"
                >
                  {allRoles.map((r) => (
                    <option key={r} value={r}>
                      {getRoleLabel(r)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer"
                >
                  {editUser ? 'Update User' : 'Create User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-sm shadow-xl">
            <div className="px-6 py-4 border-b border-navy-100">
              <h3 className="text-lg font-semibold text-navy-800">Delete User</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-navy-600 mb-1">
                Are you sure you want to delete
              </p>
              <p className="text-sm font-semibold text-navy-800 mb-4">
                "{deleteConfirm.full_name}" ({deleteConfirm.email})?
              </p>
              <p className="text-xs text-red-500 mb-5">This action cannot be undone.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors cursor-pointer"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}